import { Injectable } from '@angular/core';

/**
 * Shared single IntersectionObserver for video autoplay across the timeline.
 * Using one observer for all cards is far cheaper than one observer per card.
 * Root is null (viewport) so no shadow-DOM walk is needed.
 */
@Injectable({ providedIn: 'root' })
export class VideoAutoplayService {
  private readonly callbacks = new Map<Element, (shouldPlay: boolean) => void>();
  private readonly visibility = new Map<Element, number>();
  private activeElement: Element | null = null;
  private readonly observer: IntersectionObserver;

  constructor() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          this.visibility.set(entry.target, ratio);
        }

        this.syncPlayback();
      },
      { threshold: 0.65 },
    );
  }

  observe(el: Element, callback: (shouldPlay: boolean) => void): void {
    this.callbacks.set(el, callback);
    this.visibility.set(el, 0);
    this.observer.observe(el);
  }

  unobserve(el: Element): void {
    this.observer.unobserve(el);
    this.callbacks.delete(el);
    this.visibility.delete(el);

    if (this.activeElement === el) {
      this.activeElement = null;
      this.syncPlayback();
    }
  }

  private syncPlayback(): void {
    let nextActive: Element | null = null;
    let nextRatio = 0;

    for (const [element, ratio] of this.visibility.entries()) {
      if (ratio > nextRatio) {
        nextActive = element;
        nextRatio = ratio;
      }
    }

    this.activeElement = nextActive;
    for (const [element, callback] of this.callbacks.entries()) {
      callback(element === this.activeElement && nextRatio > 0);
    }
  }
}
